import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
  useCallback,
} from 'react';

interface AudioDataContextType {
  audioData: number;
  setAudioData: React.Dispatch<React.SetStateAction<number>>;
  audioHistory: number[];
  clearAudioHistory: () => void;
  setPaused: (paused: boolean) => void;
}

const AudioDataContext = createContext<AudioDataContextType | undefined>(undefined);

export const useAudioDataContext = (): AudioDataContextType => {
  const context = useContext(AudioDataContext);
  if (!context) {
    throw new Error('useAudioDataContext must be used within an AudioDataProvider');
  }
  return context;
};

interface AudioDataProviderProps {
  children: ReactNode;
}

const MAX_HISTORY_DURATION = 20; // seconds
const SAMPLE_RATE = 5; // samples per second (200ms intervals)
const MAX_HISTORY_LENGTH = MAX_HISTORY_DURATION * SAMPLE_RATE; // 100 samples

export const AudioDataProvider: React.FC<AudioDataProviderProps> = ({ children }) => {
  const [audioData, setAudioData] = useState<number>(0);
  const [audioHistory, setAudioHistory] = useState<number[]>([]);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const lastUpdateTime = useRef<number>(0);

  // Update audio history when audioData changes (unless paused)
  useEffect(() => {
    // Don't update history if paused
    if (isPaused) {
      return;
    }

    const now = Date.now();
    // Only add to history if enough time has passed (200ms)
    if (now - lastUpdateTime.current >= 200) {
      lastUpdateTime.current = now;

      setAudioHistory(prev => {
        const newHistory = [...prev, audioData];
        // Keep only the last 20 seconds of data
        if (newHistory.length > MAX_HISTORY_LENGTH) {
          return newHistory.slice(-MAX_HISTORY_LENGTH);
        }
        return newHistory;
      });
    }
  }, [audioData, isPaused]);

  const clearAudioHistory = useCallback(() => {
    setAudioHistory([]);
  }, []);

  const setPaused = useCallback((paused: boolean) => {
    setIsPaused(paused);
  }, []);

  return (
    <AudioDataContext.Provider
      value={{ audioData, setAudioData, audioHistory, clearAudioHistory, setPaused }}
    >
      {children}
    </AudioDataContext.Provider>
  );
};
